// libraries
import {
  KeyboardEvent,
  MouseEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

// components
import { Autocomplete, AutocompleteInputChangeReason } from '@mui/material';
import EntitySearchFilter from './components/EntitySearchFilter/EntitySearchFilter';
import { EntitySearchInput } from './components/EntitySearchInput';
import { EntitySearchOptionItem } from './components/EntitySearchOptionItem/EntitySearchOptionItem';

// constants
import { supportedPlanningEntityTypes } from './EntitySearch.consts';

// hooks
import { queries } from 'RestAPIs/rapini';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import {
  useDebounceWithStatus,
  useEntityOptionColumns
} from './EntitySearch.hooks';

//styles
import {
  EntitySearchContainer,
  EntitySearchPaper
} from './EntitySearch.styles';

// types
import { PlanningEntityIdentifier, PlanningEntityType } from 'generated/index';
import { EntitySearchOption, EntitySearchProps } from './EntitySearch.types';

// utils
import { isEqual } from 'lodash';
import { getPlanningEntityTypeByFilterCommand } from './EntitySearch.utils';
import { sortLabel } from '../AppointmentForm/AppointmentForm.utils';

const EntitySearch = ({
  initialQuery,
  filter = [],
  entityTypeFilters = [],
  activeEntities = [],
  idProperty = 'id',
  minWidth = 400,
  mode = 'default',
  inline = false,
  sx,
  emptyQuerySearchOptions,
  onChange,
  onEscape
}: EntitySearchProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const [payload, setPayload] = useState<{
    planningEntity: PlanningEntityIdentifier;
    label: string;
  } | null>(null);
  const [query, setQuery] = useState<string>(initialQuery || '');
  const [activeFilter, setActiveFilter] = useState<PlanningEntityType[]>([]);
  const [value, setValue] = useState<EntitySearchOption | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const columns = useEntityOptionColumns({ mode });

  const [debouncedQuery] = useDebounceValue(query, 300);
  const [debouncedValue, isDebouncing] = useDebounceWithStatus(value, 500);

  const { data = [] } = queries.useFindPlanningEntities(
    debouncedQuery,
    // TODO: BE needs to fix interface. BE expects a comma separated string, not an array
    (activeFilter.join(',') ||
      entityTypeFilters.join(',') ||
      undefined) as unknown as PlanningEntityType[],
    100,
    //@ts-expect-error
    ['MRP'].join(','),
    // No plan-unit narrowing here — this picker searches the whole tenant. Passed explicitly because
    // the options object has to land in the parameter after it; leaving it out would make the options
    // the plan units. Note that the @ts-expect-error above hides that mistake: TypeScript reports only
    // the first mismatching argument of a call, so a wrong one after it goes unchecked.
    undefined,
    {
      enabled: !!debouncedQuery
    }
  );

  useEffect(() => {
    const newValue = debouncedValue?.[idProperty];
    const label = debouncedValue?.label || '';

    const planningEntity: PlanningEntityIdentifier | undefined =
      debouncedValue?.isLocal
        ? debouncedValue.entity
        : (data
            .filter(({ id }) => id === newValue)
            .map(({ id, type, number }) => ({
              entityId: id,
              number,
              planningEntityType: type
            }))?.[0] as PlanningEntityIdentifier);

    if (!planningEntity) return;

    const newPayload = {
      label,
      planningEntity
    };

    if (isEqual(payload, newPayload)) return;

    setPayload(newPayload);
  }, [debouncedValue, idProperty, data, payload]);

  useEffect(() => {
    const { planningEntity, label } = payload || {};

    if (!planningEntity || !label) return;

    onChangeRef.current?.(planningEntity, label);
  }, [payload]);

  const handleChange = (_, value: EntitySearchOption | null) => setValue(value);

  const handleInputChange = (
    _event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'blur') return void setQuery('');
    if (reason !== 'reset' && reason !== 'selectOption') setQuery(value);
  };

  const handleReset = (event: MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setQuery('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSelectEntityType = (entityType: PlanningEntityType | null) => {
    if (!entityType) {
      setActiveFilter([]);
      return;
    }

    if (entityTypeFilters.length > 0 && !entityTypeFilters.includes(entityType))
      return;

    const newFilter = activeFilter.includes(entityType)
      ? activeFilter.filter((type) => type !== entityType)
      : [...activeFilter, entityType];

    setActiveFilter(newFilter);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.code == 'Escape') return void onEscape?.();

    const filterCommandPlanningEntityType =
      getPlanningEntityTypeByFilterCommand(event);
    const isCommand = query === '/' || query === '\\';

    if (
      !isCommand ||
      !inputRef.current ||
      filterCommandPlanningEntityType === 'modifier' || // Shift is allowed as a modifier key
      filter.length === 1 // We disable commands for the single filter case
    )
      return;

    event.preventDefault();
    setActiveFilter(
      filterCommandPlanningEntityType ? [filterCommandPlanningEntityType] : []
    );
    setQuery('');
    inputRef.current.value = '';
  };

  const options: EntitySearchOption[] = useMemo(
    () =>
      data
        .filter((entity) => !!entity.id)
        .filter((entity) => !activeEntities.includes(entity.id!))
        .map(({ id, birthDate, label = '', number, type }) => ({
          id: id!,
          birthDate,
          label,
          number,
          group: type && t(`entityTypeMap.${type}.single`)
        })),
    [activeEntities, data, t]
  );

  const computedOptions = useMemo(() => {
    if (query.length === 0 && !!emptyQuerySearchOptions) {
      return emptyQuerySearchOptions.options
        .filter(
          (option) =>
            (filter.length === 0 ||
              filter.includes(option.entity!.planningEntityType)) &&
            (activeFilter.length === 0 ||
              activeFilter.includes(option.entity!.planningEntityType)) &&
            (entityTypeFilters.length === 0 ||
              entityTypeFilters.includes(option.entity!.planningEntityType)) &&
            !activeEntities.includes(option.id)
        )
        .sort(sortLabel);
    }

    return options.toSorted(sortLabel);
  }, [
    query,
    emptyQuerySearchOptions,
    filter,
    activeFilter,
    entityTypeFilters,
    activeEntities,
    options,
    idProperty
  ]);

  const getGrouping = (option: EntitySearchOption) => {
    if (query.length == 0 && !!emptyQuerySearchOptions) {
      return emptyQuerySearchOptions.groupLabel;
    }

    if (filter.length === 0 || !option.group) {
      return '';
    }

    return '';
  };

  const placeholder =
    t('searchEntity') +
    (activeFilter.length > 0
      ? activeFilter
      : entityTypeFilters.length > 0
      ? entityTypeFilters
      : supportedPlanningEntityTypes
    )
      .map((x) => t(`entityTypeMap.${x}.plural`).toLowerCase())
      .join(', ');

  useEffect(() => {
    if (filter.length > 0) setActiveFilter(filter);
  }, [filter]);

  return (
    <EntitySearchContainer
      sx={sx}
      minWidth={minWidth}
      className={inline ? 'inline' : ''}
    >
      <Autocomplete
        autoHighlight
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option[idProperty].toString()}
        groupBy={getGrouping}
        filterOptions={(options) => options}
        noOptionsText={null}
        inputValue={query.toString()}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        open={query.length === 0 || !!debouncedQuery}
        options={computedOptions}
        renderInput={(params) => (
          <>
            <EntitySearchInput
              autoFocus
              params={params}
              placeholder={placeholder}
              handleReset={handleReset}
              ref={inputRef}
            />
            {filter.length !== 1 && (
              <EntitySearchFilter
                activeFilter={activeFilter}
                entityTypeFilters={entityTypeFilters}
                onSelectEntityType={handleSelectEntityType}
              />
            )}
          </>
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } =
            props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
          return (
            <EntitySearchOptionItem
              key={key}
              props={optionProps}
              option={option}
              columns={columns}
              isDebouncing={isDebouncing}
            />
          );
        }}
        slotProps={{
          popper: {
            sx: {
              '.MuiAutocomplete-noOptions': {
                display: 'none'
              }
            }
          }
        }}
        slots={{
          paper: (props) => <EntitySearchPaper {...props} />,
          popper: (props) => {
            const { style: _, ...reducedProps } = props;

            return (
              <EntitySearchPaper {...reducedProps}>
                {props.children as React.ReactNode}
              </EntitySearchPaper>
            );
          }
        }}
      />
    </EntitySearchContainer>
  );
};
export default EntitySearch;
